/**
 * Посещаемость.
 *
 * Базовый счётчик вместо внешней метрики: посещаемость страницы акции — это
 * то, за что платформа отвечает перед брендом, и считать её чужим скриптом
 * значит отдать свою цифру. Плюс внешний счётчик ставит cookie и собирает
 * профиль посетителя там, где мы обещали обратное.
 *
 * Поэтому здесь нет ни «уникальных посетителей за месяц», ни воронок по людям:
 * отпечаток посетителя живёт сутки, и связать вчерашний визит с сегодняшним
 * нельзя — это осознанное ограничение, а не недоделка.
 */
import type { VisitSlice } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Button, Card, Empty, Segmented, Skeleton, fmtShortDay, num, plural } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

/** Столько суток живут сырые посещения — дальше чистит планировщик. */
const KEEP_DAYS = 180;

/**
 * Дольше этого промежутка столбики рисуются по неделям.
 *
 * Полгода по дням — это сто восемьдесят столбиков шириной в волосок: форму
 * по ним не разглядеть, а график здесь нужен именно ради формы.
 */
const BARS_MAX = 70;

const KIND_NAMES: Record<string, string> = {
  landing: 'Страницы акций',
  embed: 'Виджеты на сайтах партнёров',
  home: 'Страница о проекте',
  app: 'Приложение покупателя',
  console: 'Кабинет партнёра',
  admin: 'Админка',
};

/** День в виде ГГГГ-ММ-ДД — в этом же виде даты ходят в запросе и в базе. */
function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Промежуток «последние n суток по сегодня», обе границы включительно. */
function lastDays(n: number): Range {
  return { from: isoDay(new Date(Date.now() - (n - 1) * 86400000)), to: isoDay(new Date()) };
}

type Range = { from: string; to: string };

export function VisitsScreen() {
  const [range, setRange] = useState<Range>(() => lastDays(30));
  const data = useResource(() => api.adminVisits(range), [range.from, range.to]);
  const d = data.data;

  // Кнопка быстрого выбора подсвечена, только если промежуток ей и равен:
  // подвинул дату руками — подсветка гаснет, и видно, что смотришь своё.
  const preset = (['7', '30', '90'] as const).find((n) => {
    const r = lastDays(Number(n));
    return r.from === range.from && r.to === range.to;
  });

  return (
    <Layout
      title="Посещения"
      subtitle="кто дошёл до страниц платформы: без cookie, без адресов и без профилей посетителей"
      action={
        <Segmented
          value={preset ?? ''}
          options={[
            { value: '7', label: 'Неделя' },
            { value: '30', label: 'Месяц' },
            { value: '90', label: '90 дней' },
          ]}
          onChange={(n) => setRange(lastDays(Number(n)))}
        />
      }
    >
      {/*
        Промежуток двумя датами, а не только «последние N суток».
        Вопрос к посещаемости обычно звучит как «что было в неделю запуска»
        или «сравни октябрь с сентябрём» — обе границы там заданы, и
        подбирать их кнопкой «90 дней» бессмысленно. Кнопки остались
        быстрым способом выставить те же две даты.
      */}
      <div className="row" style={{ gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <label className="row" style={{ gap: 6 }}>
          <span className="sub">с</span>
          <input
            className="input"
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })}
            style={{ width: 'auto' }}
          />
        </label>
        <label className="row" style={{ gap: 6 }}>
          <span className="sub">по</span>
          <input
            className="input"
            type="date"
            value={range.to}
            min={range.from}
            max={isoDay(new Date())}
            onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })}
            style={{ width: 'auto' }}
          />
        </label>
        {d && (
          <span className="sub">
            {d.days} {plural(d.days, 'день', 'дня', 'дней')}
            {d.days > KEEP_DAYS && ` · сырые посещения хранятся ${KEEP_DAYS} дней, раньше данных нет`}
          </span>
        )}
      </div>

      {data.loading && <Skeleton count={3} height={72} />}

      {d && d.totals.visits === 0 && (
        <Empty icon="chart" title="В этом промежутке посещений нет">
          Счётчик считает открытые страницы акций, виджеты на сайтах партнёров и экраны
          приложений. Возможно, стоит раздвинуть даты: за выбранные {d.days}{' '}
          {plural(d.days, 'день', 'дня', 'дней')} не открывали ничего.
        </Empty>
      )}

      {d && d.totals.visits > 0 && (
        <>
          <div className="stats" style={{ marginBottom: 18 }}>
            <Stat value={num(d.totals.visits)} label="визитов" />
            <Stat value={num(d.totals.visitors)} label="посетителей (в сутки)" />
            <Stat value={num(d.totals.landings)} label="открытий страниц акций" />
            <Stat value={num(d.totals.embeds)} label="показов виджета" />
          </div>

          <Card style={{ marginBottom: 18 }}>
            <div className="h2" style={{ marginBottom: 12 }}>
              {d.days > BARS_MAX ? 'По неделям' : 'По дням'}
            </div>
            {d.days > BARS_MAX ? (
              <Bars rows={byWeek(d.by_day)} labelOf={(r) => fmtShortDay(`${r.key}T00:00:00Z`)} />
            ) : (
              <Bars rows={d.by_day} labelOf={(r) => fmtShortDay(`${r.key}T00:00:00Z`)} />
            )}
          </Card>

          <div className="grid c2">
            <Card>
              <div className="h2" style={{ marginBottom: 10 }}>
                Страны
              </div>
              {d.by_country.length === 0 ? (
                <p className="sub">Посещений за этот срок не было.</p>
              ) : (
                <>
                  <List rows={d.by_country} nameOf={(r) => r.label || r.key} />
                  {d.by_country.every((r) => !r.key) && (
                    <p className="sub" style={{ marginTop: 10, fontSize: 11.5 }}>
                      Страна берётся из заголовка обратного прокси — у Cloudflare это
                      CF-IPCountry. Пока домен не переведён на Cloudflare, проставлять его
                      некому, и страна пустая у всех посещений. Своей базы адресов у платформы
                      нет, а выдумывать страну по языку браузера нечестно: человек в Худжанде с
                      английским браузером не становится британцем.
                    </p>
                  )}
                </>
              )}
            </Card>

            <Card>
              <div className="h2" style={{ marginBottom: 10 }}>
                Где смотрят
              </div>
              <List rows={d.by_kind} nameOf={(r) => KIND_NAMES[r.key] ?? r.key} />
            </Card>

            <Card>
              <div className="h2" style={{ marginBottom: 10 }}>
                Откуда приходят
              </div>
              {d.by_ref.length === 0 ? (
                <p className="sub">
                  Переходов со стороны не было — или браузеры не передали источник. Внутренние
                  переходы между своими же страницами источником не считаются.
                </p>
              ) : (
                <List rows={d.by_ref} nameOf={(r) => r.key} />
              )}
            </Card>

            <Card>
              <div className="h2" style={{ marginBottom: 10 }}>
                Акции
              </div>
              {d.by_campaign.length === 0 ? (
                <p className="sub">Страницы акций пока не открывали.</p>
              ) : (
                <List rows={d.by_campaign} nameOf={(r) => r.label || r.key} />
              )}
            </Card>
          </div>
        </>
      )}

      <Backups />
    </Layout>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="stat">
      <div className="v">{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

/**
 * Свёртка дней в недели — для длинных промежутков.
 *
 * Посетители складываются так же, как визиты, и это не ошибка счёта:
 * отпечаток посетителя живёт сутки и назавтра у того же человека другой.
 * «Уникальных за неделю» в этих данных не существует по устройству, и
 * единственное честное недельное число — сумма суточных.
 */
function byWeek(days: VisitSlice[]): VisitSlice[] {
  const out: VisitSlice[] = [];
  const index = new Map<string, VisitSlice>();
  for (const day of days) {
    const d = new Date(`${day.key}T00:00:00Z`);
    // Неделя считается от понедельника: воскресенье у JS нулевое.
    const shift = (d.getUTCDay() + 6) % 7;
    const key = new Date(d.getTime() - shift * 86400000).toISOString().slice(0, 10);
    const row = index.get(key);
    if (row) {
      row.visits += day.visits;
      row.visitors += day.visitors;
      continue;
    }
    const fresh: VisitSlice = { key, visits: day.visits, visitors: day.visitors };
    index.set(key, fresh);
    out.push(fresh);
  }
  return out;
}

/** Столбики по дням: разглядывают форму, а не точные значения. */
function Bars({ rows, labelOf }: { rows: VisitSlice[]; labelOf: (r: VisitSlice) => string }) {
  const max = Math.max(1, ...rows.map((r) => r.visits));
  return (
    <div className="vbars">
      {rows.map((r) => (
        <div
          key={r.key}
          className="vbar"
          title={`${labelOf(r)}: ${r.visits} ${plural(r.visits, 'визит', 'визита', 'визитов')}, ${r.visitors} ${plural(r.visitors, 'посетитель', 'посетителя', 'посетителей')}`}
        >
          <i style={{ height: `${Math.max(3, Math.round((100 * r.visits) / max))}%` }} />
          <span>{labelOf(r)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Строка разреза: визиты и посетители рядом.
 *
 * Два числа, а не одно: визиты говорят, сколько раз открывали, посетители —
 * сколько человек это сделало. Разрыв между ними и есть ответ на вопрос,
 * ходит ли к нам много людей по разу или один человек двадцать раз, и по
 * одному числу его не видно.
 *
 * Столбик рисуется по посетителям: в рейтинге «откуда заходят» важнее люди,
 * а не перезагрузки страницы.
 */
function List({ rows, nameOf }: { rows: VisitSlice[]; nameOf: (r: VisitSlice) => string }) {
  const max = Math.max(1, ...rows.map((r) => r.visitors || r.visits));
  return (
    <div>
      {/* Шапка таблицы без полоски: пустая дорожка над первой строкой
          читается как строка с нулём, которой там нет. */}
      <div className="datarow two sub" style={{ fontSize: 11.5 }}>
        <div />
        <span />
        <span className="mono">посетителей</span>
        <span className="mono">визитов</span>
      </div>
      {rows.map((r) => (
        <div className="datarow two" key={r.key || 'unknown'}>
          <div>{nameOf(r)}</div>
          <span className="bar">
            <i style={{ width: `${Math.round((100 * (r.visitors || r.visits)) / max)}%` }} />
          </span>
          <span className="mono">{num(r.visitors)}</span>
          <span className="mono sub">{num(r.visits)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Ежедневные копии данных участников.
 *
 * Отдельно от бэкапа базы целиком: база — забота того, кто её держит, а это
 * копия ровно тех данных, за которые платформа отвечает перед людьми.
 */
function Backups() {
  const { canModerate } = useAdmin();
  const toast = useToast();
  const data = useResource(() => api.adminBackups(), []);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { name } = await api.adminRunBackup();
      toast(`Копия готова: ${name}`);
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const list = data.data?.backups ?? [];

  return (
    <Card style={{ marginTop: 18 }}>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="h2">Резервные копии участников</span>
        <span className="spacer" />
        {canModerate && (
          <Button size="sm" icon="download" disabled={busy} onClick={() => void run()}>
            Сделать сейчас
          </Button>
        )}
      </div>
      <p className="sub">
        Раз в сутки платформа складывает копию данных участников — сами участники, анкеты,
        согласия, билеты, исходы, призы и конкурсные работы. Хранится{' '}
        {data.data?.keep_days ?? 30} дней: личные данные, лежащие дольше, чем нужно, — это риск
        без пользы. Каталог: <span className="mono">{data.data?.dir || '—'}</span>
      </p>

      {list.length === 0 ? (
        <p className="sub" style={{ marginTop: 10 }}>
          Копий пока нет — первая появится при ближайшем проходе планировщика.
        </p>
      ) : (
        <div style={{ marginTop: 12 }}>
          {list.map((f) => (
            <div className="datarow" key={f.name}>
              <div className="mono">{f.name}</div>
              <span className="bar" />
              <span className="mono">{(f.size / 1024).toFixed(0)} КБ</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
